<template>
  <div class="container !max-w-[1200px]">
    <div class="flex flex-wrap">
      <div class="w-12/12">
        <div class="mt-4">
          <!-- License Selection -->
          <div
            class="flex flex-wrap justify-center roboto h-fit md:h-screen overflow-hidden"
          >
            <div class="license-height-wrapper w-12/12 max-w-[1100px] md:flex justify-between">
              <div class="text-wrapper">
                <p class="bold mb-0 steps">Step 1 of {{ totalSteps }}</p>
                <h3 class="mb-1/2 bold text-blue-dark md:mt-0">
                  {{ webstoreContent.cartPage.choose_license }}
                </h3>

                <!-- Contract info -->
                <div class="mb-1/2 text-base leading-tight" v-if="showContractDisclaimer && !hasContractExpired">
                    <div class="">
                        {{ contractDisclaimer }}
                    </div>
                </div>

                <!-- Safety Event Review info -->
                <div class="mb-1/2 text-base" v-if="isSafetyEventReviewActive">
                    <div class="safety-event-review">
                        <strong>Please note: </strong>{{ webstoreContent.cartPage.safety_event_review.replace("$5", `$${safetyEventReviewPrice.toFixed(2)}`) }}
                    </div>
                </div>

                <span
                  v-if="activeProductKey.includes('CM')"
                  @click="openLicense = !openLicense"
                  class="text-blue-default text-base font-bold no-underline mt-1/2 hover:no-underline cursor-pointer"
                >
                  <Plus />
                  <span class="">
                    {{ webstoreContent.webstoreBody.license.link.text }}
                  </span>
                </span>
                <LicensePopup
                  v-if="openLicense"
                  :content="webstoreContent.webstoreBody.license"
                  @close="openLicense = !openLicense"
                />
              </div>
              <div class="w-full md:w-6/12 pt-1 md:pt-0 md:px-1 md:overflow-y-scroll scroll-wrapper">
                <LicenseSelection
                  @scroll-to="scrollTo"
                  @license-change="handleLicenseChange"
                />
              </div>
            </div>
          </div>

          <!-- Quantity Selection -->
          <div
            ref="bagStep1"
            class="quantity-wrapper flex flex-wrap justify-center roboto mt-3 md:mt-5 quantity-selection h-screen"
            :class="bagStep < 1 && 'grayed'"
          >
            <div v-if="bagStep < 1" class="cover-up"></div>
            <div class="w-12/12 max-w-[1100px] md:flex justify-between">
              <div
                class="w-full md:w-5/12"
              >
                <p class="bold mb-0 pt-1 steps">Step 2 of {{ totalSteps }}</p>
                <h3 class="bold text-blue-dark mt-0">
                  {{ webstoreContent.cartPage.quantityCopy }}
                </h3>
              </div>
              <div class="offer-wrapper w-full md:w-6/12 md:p-1 md:pt-0 md:overflow-hidden">
                <div
                  :key="offer"
                  class="w-full"
                  v-for="(offer_info, offer) in this.activeProductInfo
                    .offerings"
                >
                  <ProductQuantity
                    v-if="offer_info.offerKey === activeOfferKey"
                    :ref="cleanOfferKey(offer_info.offerKey)"
                    type="product"
                    :showImage="false"
                    :updateTotal="true"
                    :imageSource="offer_info.offerImage"
                    :productName="activeProductInfo.name"
                    :skuText="offer_info.productCodes.join(', ')"
                    :salesPrice="activeProductInfo.offerings[offer].salesPrice"
                    :defaultTerm="activeOffer.defaultPaymentTerm"
                    :currency="currencySymbol"
                    :maxPurchasableQuantity="
                      offer_info.maxPurchasableQuantity
                    "
                    :minPurchasableQuantity="1"
                    :startingNumber="
                      setInitialOfferQuantity
                    "
                    :value="`${offer} Quantity`"
                    :maxError="webstoreContent.cartPage.max_quantity_error"
                  />
                </div>
                <div class="button-wrapper flex justify-center pt-1">
                  <span
                    class="border-1 border-blue-default rounded-full text-xl font-bold text-blue-default py-1/2 px-2 cursor-pointer hover:bg-blue-default hover:text-white"
                    :class="cablesNeededCount !== 0 ? 'active' : 'inactive'"
                    @click="scrollTo(doesOfferRequireCable(activeOfferKey) ? 2 : 4)"
                  >
                    {{ webstoreContent.cartPage.next }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Cable Selection -->
          <div>
            <CableSelection
              v-if="doesOfferRequireCable(activeOfferKey)"
              class="roboto bagStep2"
              ref="cable-selection"
              :addToCart="true"
              :bag-step="bagStep"
              :productName="activeProductInfo.name"
              :totalSteps="totalSteps"
              :activeCableSelectionMethods="activeCableSelectionMethods"
              @updateBagStep="updateBagStep"
              @scroll-to="scrollTo"
              @add-selection-method="updateActiveCableSelectionMethods"
              @remove-selection-method="removeActiveCableMethod"
              @update-bottom-bar="updateBottomBar"
              @update-bottom-bar-action="updateBarActions"
            />
          </div>

          <!-- Selection Summary -->
          <SelectionSummary
            ref="selection-summary"
            :submitButtonText="submitButtonText"
            :addToCartDisabled="addToCartDisabled"
            :totalSteps="totalSteps"
          />
        </div>
      </div>
      <ActionBottomNav
        ref="bottom-nav"
        :submitButtonText="submitButtonText"
        :totalSteps="totalSteps"
        :activeCableSelectionMethods="activeCableSelectionMethods"
        :bottomNavIsShown="bottomNavIsShown"
        :isOnCableSelection="isOnCableSelection"
        @scroll-to="scrollTo"
        @add-to-cart="addToCart"
        @add-selection-method="updateActiveCableSelectionMethods"
        @confirm-cables="confirmCables"
      />
    </div>
  </div>
</template>
<script>
import Plus from '../../../utils/icons/plus.vue';
import ProductQuantity from "../../webstore/product-quantity";
import SelectionSummary from "../../webstore/selection-summary";
import CableSelection from "../../../cable-selection/cable-selection";
import LicenseSelection from '../components/LicenseSelection.vue';
import LicensePopup from "../components/LicensePopup.vue";
import ActionBottomNav from "../components/ActionBottomNav.vue"
import { cableSelectionAvailableOptions } from "../../../../settings";
import { WebstoreResources } from "../../../../mixins/webstore-resources";

export default {
  mixins: [WebstoreResources],
  components: {
    Plus,
    ProductQuantity,
    CableSelection,
    LicensePopup,
    SelectionSummary,
    ActionBottomNav,
    LicenseSelection,
  },
  props: {
    content: {
      type: Object,
      required: true,
    },
    submitButtonText: {
      type: String,
      required: true
    },
    addToCartDisabled: {
      type: Boolean,
      required: false
    },
    bottomNavIsShown: {
      type: Boolean,
      required: true
    },
  },
  data() {
    return {
      openLicense: false,
      activeCableSelectionMethods: [],
      isOnCableSelection: false,
      cableSelectionAvailableOptions: cableSelectionAvailableOptions,
    };
  },
  computed: {
    isProductKeyVGCBL() {
      return this.activeProductKey === "VG-CBL";
    },
    setInitialOfferQuantity() {
      let offerInCart = this.productsInCart.find((offerInCart) => offerInCart.productKey === this.activeProductKey && offerInCart.offerKey === this.activeOfferKey);
      if (offerInCart) {
        return offerInCart.quantity
      }
      return this.$store.getters[`${this.activeProductKey}/quantity`];
  
    },
  },
  methods: {

    handleLicenseChange(offer) {
      let offerInCart = this.productsInCart.find((offerInCart) => offerInCart.productKey === this.activeProductKey && offerInCart.offerKey === offer);
      let quantity = 1;
      if (offerInCart) {
        quantity = offerInCart.quantity
      }
      this.$store.commit(`${this.activeProductKey}/setQuantity`, quantity);
      this.setInitialOfferQuantity;
    },

    scrollTo(bagStep) {
      // Reset isOnCableSelection if we switch license selection
      if (bagStep === 1) {
        this.isOnCableSelection = false
      }
      let refName = `bagStep${bagStep}`;
      let element;
      if (bagStep > 3) {
        element = this.$refs['selection-summary'].$refs[refName];
      } else if (bagStep > 1) {
        element = this.$refs['cable-selection'].$refs[refName];
      } else {
        element = this.$refs[refName];
      }

      if (element) {
        var top = element.offsetTop - 130;

        window.scroll({
          left: 0,
          top,
          behavior: 'smooth',
        });
      }
      this.setBagStep(bagStep);
    },

    addToCart() {
      this.$emit("add-to-cart");
    },

    updateBottomBar(shown) {
      this.bottomNavIsShown = shown;
    },

    updateBarActions(cableSelection) {
      this.isOnCableSelection = cableSelection
    },

    updateActiveCableSelectionMethods(method, reset) {
      if (reset) {
        this.activeCableSelectionMethods = [method]
      } else if (!this.activeCableSelectionMethods.includes(method)) {
        this.activeCableSelectionMethods.push(method);
        let t = this;
        let useIndex = this.activeCableSelectionMethods.length - 1
        if (useIndex > 0) {
          useIndex = useIndex - 1
        }
        let offset = this.activeCableSelectionMethods[useIndex] === 'plug type' ? 650 : -100;
        if (method === 'plug type') {
          offset = -900
        }
        setTimeout(() => {
          let height = t.$refs['cable-selection'].$refs[method][0]['$el'].offsetHeight + offset;
          this.$refs['cable-selection'].$refs['desktopScrollWrapper'].scrollTo({top: height, left: 0, behavior: 'smooth'});
        }, 200)
      }
    },

    removeActiveCableMethod(method) {
      if (this.activeCableSelectionMethods.includes(method)) {
        this.activeCableSelectionMethods.splice(this.activeCableSelectionMethods.indexOf(method), 1);
      }
    },
  },
  mounted() {
    this.isOnCableSelection = false;
    // Auto scroll past step 1 if they can only select replacement hardware
    if (this.hideLicenses) {
      setTimeout(() => this.scrollTo(1), 300);
    }
  }
};
</script>
<style lang="scss" scoped>

  .steps {
    color: #8390A0;
  }

  .disclaimer {
    font-size: 18px;
    line-height: 20px;
    color: #474f54;
  }
  .grayed {
    opacity: 0.4;
    overflow: hidden;
  }
  .cover-up {
    height: 100%;
    width: 100%;
    position: absolute;
    z-index: 10;
    left: 0;
  }
  .inactive {
    opacity: 0.4;
  }
  .inactive:hover {
    color: rgb(1, 132, 251) !important;
    background: white !important;
  }
  .quantity-wrapper {
    margin-top: 300px;
    padding-top: 50px;
  }

  .safety-event-review {
    padding: 15px;
    background-color: #F0F6FE;
    font-style: italic;
    font-size: 14px;
    margin-bottom: 20px;
    line-height: 18px;
  }

  .active {
    border: 3px solid #0384fb;
  }
  .license-height-wrapper {
    height: 100%;
  }
  @media only screen and (max-width: 736px) {
    .quantity-selection {
      padding-top: 0 !important;
      margin-bottom: 50px;
    }
    .scroll-wrapper {
      display: flex;
      justify-content: center;
    }
    #offer-list {
      min-height: 315px;
    }
    #cables-submitted-count {
      order: -1;
      justify-content: center;
    }
    #add-product-button-container {
      justify-content: flex-end;
    }

    #offer-container {
      width: 95%;
    }
    .text-wrapper {
      text-align: center;
    }
  }
</style>